import React, { useRef, useEffect, useState } from "react";
import { Star } from "lucide-react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/outline";
import toJalali from "../utils/Time";
import User from "../assets/images/Frame 440.png";
import NoImage from "../assets/images/no-image-icon-23485.png";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import CommentSkeleton from "./CommentSkeleton";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getImageUrl = (fileUrl?: string | null) => {
  if (!fileUrl) return NoImage;
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  return `${BASE_URL ?? ""}${fileUrl.startsWith("/") ? "" : "/"}${fileUrl}`;
};

export interface UserCommentReview {
  id: string | number;
  name: string;
  date: string;
  rate: number;
  comment: string;
  place: string;
  city: string;
  img: string;
  placeId: string;
}

const HOME_REVIEWS_CACHE_DURATION = 10 * 60 * 1000;
const HOME_REVIEWS_PAGE_SIZE = 20;
let cachedHomeReviews: { reviews: UserCommentReview[]; cachedAt: number } | null = null;
let homeReviewsRequest: Promise<UserCommentReview[]> | null = null;
const reviewedPlaceRequests = new Map<string, Promise<any | null>>();

const fetchReviewedPlace = (placeId: string) => {
  const cachedRequest = reviewedPlaceRequests.get(placeId);
  if (cachedRequest) return cachedRequest;

  const request = fetch(`${BASE_URL}/api/places/${placeId}/`)
    .then(async (response) => {
      if (!response.ok) throw new Error("Failed to fetch reviewed place");
      const body = await response.json();
      return body.data ?? body;
    })
    .catch(() => null);

  reviewedPlaceRequests.set(placeId, request);
  return request;
};

const fetchHomeReviews = () => {
  if (cachedHomeReviews && Date.now() - cachedHomeReviews.cachedAt < HOME_REVIEWS_CACHE_DURATION) {
    return Promise.resolve(cachedHomeReviews.reviews);
  }

  if (homeReviewsRequest) return homeReviewsRequest;

  homeReviewsRequest = (async () => {
    const reviewsResponse = await fetch(
      `${BASE_URL}/api/reviews/?shuffle=true&page_size=${HOME_REVIEWS_PAGE_SIZE}`,
    );

    if (!reviewsResponse.ok) {
      if (reviewsResponse.status === 429) {
        throw new Error("دریافت نظرات موقتاً محدود شده است. لطفاً چند لحظه دیگر تلاش کنید.");
      }
      throw new Error("Failed to fetch reviews");
    }

    const reviewsData = await reviewsResponse.json();
    const reviewsList = (reviewsData.results ?? reviewsData.data ?? []).filter((review: any) => review.rate >= 4);

    const reviews = await Promise.all(
      reviewsList.map(async (review: any): Promise<UserCommentReview> => {
        const rawPlace = review.place;
        const placeId = typeof rawPlace === "object" && rawPlace !== null
          ? rawPlace.place_id ?? rawPlace.id
          : rawPlace;
        const place = placeId ? await fetchReviewedPlace(String(placeId)) : null;

        return {
          id: review.review_id || `${placeId}-${review.date}`,
          name: review.user_name === "Unknown" ? "کاربر سایت" : (review.user_name || "کاربر"),
          date: review.date || "",
          rate: review.rate || 0,
          comment: review.comment || "کاربر تجربه خود را ثبت کرده است.",
          placeId: placeId ? String(placeId) : "",
          place: place?.name || review.place_name || rawPlace?.name || "مکان نامشخص",
          city: place?.cities?.[0]?.name ||
            (typeof place?.city === "string" ? place.city : place?.city?.name) ||
            review.city_name ||
            "شهر نامشخص",
          img: getImageUrl(place?.images?.[0]?.file_url),
        };
      }),
    );

    cachedHomeReviews = { reviews, cachedAt: Date.now() };
    return reviews;
  })().finally(() => {
    homeReviewsRequest = null;
  });

  return homeReviewsRequest;
};

export const UserCommentCard: React.FC<{
  review: UserCommentReview;
  className?: string;
  fluid?: boolean;
  onReviewClick?: (review: UserCommentReview) => void;
}> = ({ review, className = "", fluid = false, onReviewClick }) => (
  <article className={`flex h-[250px] ${fluid ? "w-full min-w-0" : "w-[343px] min-w-[313px] shrink-0 tablet:min-w-[343px]"} flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-[0_6px_20px_rgba(0,0,0,0.06)] tablet:h-[252px] ${className}`}>
    <Link
      to={`/places/${review.placeId}`}
      className="flex min-w-0 items-center gap-3 pb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]"
    >
      <img
        src={review.img}
        alt={review.place}
        className="h-14 w-14 shrink-0 rounded-xl object-cover"
        onError={(event) => {
          event.currentTarget.src = NoImage;
        }}
      />
      <div className="min-w-0 text-right">
        <h3 className="truncate text-sm text-[#222222] font-myYekanFaNumDemiBold">
          {typeof review.place === "string" ? review.place : "مکان نامشخص"}
        </h3>
        <p className="mt-1 truncate text-[11px] text-gray-500 font-myYekanRegular">
          {review.city}
        </p>
      </div>
    </Link>

    {onReviewClick ? (
      <button
        type="button"
        onClick={() => onReviewClick(review)}
        className="min-h-0 flex-1 border-y border-gray-100 py-3 text-right focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#038545]"
        aria-label={`مشاهده متن کامل نظر درباره ${review.place}`}
      >
        <p className="line-clamp-3 text-justify text-[13px] leading-6 text-gray-700 font-myYekanRegular">
          {typeof review.comment === "string" ? review.comment : "کاربر تجربه خود را ثبت کرده است."}
        </p>
      </button>
    ) : (
      <div className="min-h-0 flex-1 border-y border-gray-100 py-3">
        <p className="line-clamp-3 text-justify text-[13px] leading-6 text-gray-700 font-myYekanRegular">
          {typeof review.comment === "string" ? review.comment : "کاربر تجربه خود را ثبت کرده است."}
        </p>
      </div>
    )}

    {onReviewClick ? (
      <button
        type="button"
        onClick={() => onReviewClick(review)}
        className="flex shrink-0 items-center justify-between gap-2 rounded-b-xl pt-3 text-right transition-colors hover:text-[#038545] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#038545]"
        aria-label={`مشاهده متن کامل نظر ${review.name}`}
      >
        <ReviewAuthorAndRating review={review} />
      </button>
    ) : (
      <div className="flex shrink-0 items-center justify-between gap-2 pt-3">
        <ReviewAuthorAndRating review={review} />
      </div>
    )}
  </article>
);

const ReviewAuthorAndRating: React.FC<{ review: UserCommentReview }> = ({ review }) => (
  <>
    <div className="flex min-w-0 items-center gap-2">
      <img
        src={User}
        alt=""
        className="h-9 w-9 shrink-0 rounded-full object-cover"
      />
      <div className="flex min-w-0 items-center gap-2">
        <p className="max-w-[90px] truncate text-xs text-[#222222] font-myYekanFaNumDemiBold">
          {typeof review.name === "string" ? review.name : "کاربر سایت"}
        </p>
        <time className="shrink-0 text-[10px] text-gray-600 font-myYekanFaNumRegular">
          {toJalali(review.date)}
        </time>
      </div>
    </div>

    <div
      className="flex shrink-0 items-center gap-0.5"
      dir="ltr"
      aria-label={`امتیاز ${review.rate} از ۵`}
    >
      {Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          className={`h-3.5 w-3.5 ${
            index < Math.round(review.rate)
              ? "fill-[#038545] text-[#038545]"
              : "text-gray-300"
          }`}
          aria-hidden="true"
        />
      ))}
    </div>
  </>
);

export const ReviewDetailsModal: React.FC<{ review: UserCommentReview; onClose: () => void }> = ({ review, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="review-details-title">
      <button
        type="button"
        aria-label="بستن نمایش نظر"
        onClick={onClose}
        className="absolute inset-0 bg-black/45"
      />
      <article className="relative z-10 flex max-h-[min(620px,calc(100vh-2rem))] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-[0_18px_48px_rgba(0,0,0,0.2)]">
        <button
          type="button"
          onClick={onClose}
          aria-label="بستن"
          className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#038545]"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
        <Link to={`/places/${review.placeId}`} onClick={onClose} className="flex min-w-0 items-center gap-3 border-b border-gray-100 pb-3 pl-9 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]">
          <img
            src={review.img}
            alt={review.place}
            className="h-14 w-14 shrink-0 rounded-xl object-cover"
            onError={(event) => {
              event.currentTarget.src = NoImage;
            }}
          />
          <div className="min-w-0 text-right">
            <h2 id="review-details-title" className="truncate text-sm text-[#222222] font-myYekanFaNumDemiBold">
              {review.place}
            </h2>
            <p className="mt-1 truncate text-[11px] text-gray-500 font-myYekanRegular">{review.city}</p>
          </div>
        </Link>
        <div className="min-h-0 flex-1 overflow-y-auto py-4">
          <p className="whitespace-pre-wrap text-justify text-sm leading-7 text-gray-700 font-myYekanRegular">
            {typeof review.comment === "string" ? review.comment : "کاربر تجربه خود را ثبت کرده است."}
          </p>
        </div>
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-100 pt-3">
          <ReviewAuthorAndRating review={review} />
        </div>
      </article>
    </div>,
    document.body,
  );
};

const UserComments: React.FC = () => {
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const [reviews, setReviews] = useState<UserCommentReview[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedReview, setSelectedReview] = useState<UserCommentReview | null>(null);

  const scrollDesktopNext = () =>
    desktopScrollRef.current?.scrollBy({ left: -600, behavior: "smooth" });
  const scrollDesktopPrev = () =>
    desktopScrollRef.current?.scrollBy({ left: 600, behavior: "smooth" });

  useEffect(() => {
    if (cachedHomeReviews && Date.now() - cachedHomeReviews.cachedAt < HOME_REVIEWS_CACHE_DURATION) {
      setReviews(cachedHomeReviews.reviews);
      setLoading(false);
      return;
    }

    let isActive = true;

    const fetchReviewsAndPlaces = async () => {
      setLoading(true);
      try {
        const fetchedReviews = await fetchHomeReviews();
        if (isActive) setReviews(fetchedReviews);
        // کد قدیمی دریافت مستقیم نظرات در ادامه عمداً غیرفعال است؛ fetchHomeReviews مسیر فعال و مشترک است.
        return;

        /* مسیر قدیمی دریافت مستقیم و غنی‌سازی نظرات؛ fetchHomeReviews جایگزین آن شده است.
        const reviewsResponse = await fetch(`${BASE_URL}/api/reviews/?shuffle=true&page_size=10`);

        if (!reviewsResponse.ok) throw new Error("Failed to fetch reviews");

        const reviewsData = await reviewsResponse.json();
        const reviewsList = reviewsData.results || [];

        // اصلاح شده: دسترسی به آرایه نظرات از طریق فیلد results
        const reviewsWithImages = await Promise.all(
          reviewsList.map(async (r: any) => {
            // *** تغییر اصلی: بررسی امتیاز ***
            if (r.rate < 4) {
              return null; // اگر امتیاز کمتر از ۴ است، این آیتم را حذف کن (null برگردان)
            }

            const rawPlace = r.place;
            const placeId = typeof rawPlace === "object" && rawPlace !== null
              ? rawPlace.place_id ?? rawPlace.id
              : rawPlace;
            let placeImg = NoImage;
            let placeName = r.place_name || rawPlace?.name || "مکان نامشخص";
            let cityName =
              r.city_name ||
              rawPlace?.cities?.[0]?.name ||
              (typeof rawPlace?.city === "string" ? rawPlace.city : rawPlace?.city?.name) ||
              "شهر نامشخص";

            try {
              if (!placeId) throw new Error("Review has no place id");

              const placeResponse = await fetch(`${BASE_URL}/api/places/${placeId}/`);
              if (placeResponse.ok) {
                const placeBody = await placeResponse.json();
                const placeData = placeBody.data ?? placeBody;
                const firstImage = placeData.images?.[0]?.file_url;

                placeImg = getImageUrl(firstImage);
                placeName = placeData.name || placeName;
                cityName =
                  placeData.cities?.[0]?.name ||
                  (typeof placeData.city === "string" ? placeData.city : placeData.city?.name) ||
                  cityName;
              }
            } catch (err) {
              console.error(`Failed to fetch place for review ${r.review_id}:`, err);
            }
            return {
              id: r.review_id || Math.random().toString(),
              name: r.user_name === "Unknown" ? "کاربر سایت" : (r.user_name || "کاربر"),
              date: r.date || "",
              rate: r.rate || 0,
              comment: r.comment || "کاربر تجربه خود را ثبت کرده است.",
              placeId: placeId ? String(placeId) : "",
              place: placeName,
              city: cityName,
              img: placeImg,
            };
          })
        );

        // حذف مقادیر null (نظراتی که امتیازشان کمتر از ۴ بود) از آرایه نهایی
        const filteredReviews = reviewsWithImages.filter((r) => r !== null) as UserCommentReview[];
        
        if (isActive) {
          cachedHomeReviews = { reviews: filteredReviews, cachedAt: Date.now() };
          setReviews(filteredReviews);
        }
        */
      } catch (err) {
        console.error("Failed to fetch reviews:", err);
      } finally {
        if (isActive) setLoading(false);
      }
    };
    fetchReviewsAndPlaces();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    const scrollContainer = desktopScrollRef.current;
    if (!scrollContainer) return;

    const updateScrollAvailability = () => {
      const containerBounds = scrollContainer.getBoundingClientRect();
      const cards = Array.from(scrollContainer.children) as HTMLElement[];

      setCanScrollPrev(
        cards.some((card) => card.getBoundingClientRect().right > containerBounds.right + 1)
      );
      setCanScrollNext(
        cards.some((card) => card.getBoundingClientRect().left < containerBounds.left - 1)
      );
    };

    const animationFrame = window.requestAnimationFrame(updateScrollAvailability);
    const resizeObserver = new ResizeObserver(updateScrollAvailability);

    resizeObserver.observe(scrollContainer);
    scrollContainer.addEventListener("scroll", updateScrollAvailability, { passive: true });
    window.addEventListener("resize", updateScrollAvailability);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      scrollContainer.removeEventListener("scroll", updateScrollAvailability);
      window.removeEventListener("resize", updateScrollAvailability);
    };
  }, [reviews, loading]);

  const getArrowClassName = () =>
    "flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-800 shadow-sm opacity-0 transition-opacity duration-200 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#038545] pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100";

  const renderSkeletons = () => {
    return Array.from({ length: 4 }).map((_, index) => (
      <CommentSkeleton key={`skeleton-${index}`} />
    ));
  };

  return (
    <section className="site-container py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 tablet:px-12 desktop:px-0">
        <h2 className="text-lg tablet:text-xl desktop:text-2xl font-myYekanFaNumExtraBold text-center">
          نظرات کاربران
        </h2>
        <Link
          to="/reviews"
          className="flex items-center gap-1 text-[12px] text-gray-800 font-myYekanFaNumMedium"
        >
          مشاهده همه
          <ChevronLeftIcon className="h-4 w-4" />
        </Link>
      </div>
      {/* Mobile view */}
      <div className="tablet:hidden relative font-myYekanFaNumRegular">
        <div className="px-0 relative">
          <div
            ref={mobileScrollRef}
            className="flex overflow-x-auto pb-6 space-x-2 rtl:space-x-reverse scroll-smooth scrollbar-hide"
          >
            {loading ? (
              renderSkeletons()
            ) : (
              reviews.map((review) => (
                <UserCommentCard key={review.id} review={review} onReviewClick={setSelectedReview} />
              ))
            )}
          </div>
        </div>
      </div>
      {/* Desktop view */}
      <div className="group hidden tablet:block relative font-myYekanFaNumRegular">
        {canScrollPrev && (
          <button
            onClick={scrollDesktopPrev}
            className={`${getArrowClassName()} absolute right-3 top-1/2 z-10 -translate-y-1/2`}
            aria-label="مشاهده نظرات قبلی"
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>
        )}
        <div
          ref={desktopScrollRef}
          className="flex overflow-x-auto pb-6 scrollbar-hide space-x-6 rtl:space-x-reverse scroll-smooth"
        >
          {loading ? (
            renderSkeletons()
          ) : (
            reviews.map((review) => (
              <UserCommentCard key={review.id} review={review} onReviewClick={setSelectedReview} />
            ))
          )}
        </div>
        {canScrollNext && (
          <button
            onClick={scrollDesktopNext}
            className={`${getArrowClassName()} absolute left-3 top-1/2 z-10 -translate-y-1/2`}
            aria-label="مشاهده نظرات بعدی"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {selectedReview && <ReviewDetailsModal review={selectedReview} onClose={() => setSelectedReview(null)} />}

      {/* Hide scrollbar styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

export default UserComments;
